import { useMemo } from 'react';

interface Point { t: string; price: number }

interface Props {
  data: Point[];
  width?: number;
  height?: number;
  sparkline?: boolean;
  color?: string;
  showHighestLabel?: boolean;
}

export function PriceChart({ data, width = 320, height = 120, sparkline = false, color, showHighestLabel = false }: Props) {
  const { path, area, minP, maxP, last, first, ticks, highPoint } = useMemo(() => {
    if (!data.length) return { path: '', area: '', minP: 0, maxP: 0, last: 0, first: 0, ticks: [], highPoint: null };

    const prices = data.map((d) => d.price);
    const minP = Math.min(...prices);
    const maxP = Math.max(...prices);
    const range = maxP - minP || 1;
    const pad = sparkline ? 4 : 12;

    const toX = (i: number) => pad + (i / (data.length - 1)) * (width - pad * 2);
    const toY = (p: number) => pad + ((maxP - p) / range) * (height - pad * 2);

    const pts = data.map((d, i) => `${toX(i)},${toY(d.price)}`);
    const path = `M ${pts.join(' L ')}`;
    const area = `M ${pts[0]} L ${pts.join(' L ')} L ${toX(data.length - 1)},${height - pad} L ${toX(0)},${height - pad} Z`;
    const highIndex = prices.reduce((bestIdx, price, idx) => (price > prices[bestIdx]! ? idx : bestIdx), 0);
    const highPoint = {
      x: toX(highIndex),
      y: toY(prices[highIndex]!),
      price: prices[highIndex]!,
    };

    const labelCount = 4;
    const ticks: { label: string; x: number }[] = [];
    for (let i = 0; i < labelCount; i++) {
      const idx = Math.round((i / (labelCount - 1)) * (data.length - 1));
      ticks.push({ label: data[idx]!.t.slice(5, 10), x: toX(idx) });
    }

    return { path, area, minP, maxP, last: prices[prices.length - 1]!, first: prices[0]!, ticks, highPoint };
  }, [data, width, height, sparkline]);

  if (!data.length) {
    return (
      <div className="flex items-center justify-center text-xs text-text-muted" style={{ height }}>
        暂无数据
      </div>
    );
  }

  const rising = last >= first;
  const lineColor = color ?? (rising ? '#56d364' : '#f85149');
  const areaId = `area-${lineColor.replace('#', '')}`;
  const glowId = `highest-glow-${lineColor.replace('#', '')}`;
  const tooltipWidth = 52;
  const tooltipHeight = 19;
  const tooltipX = highPoint ? Math.min(Math.max(highPoint.x + 8, 2), width - tooltipWidth - 2) : 0;
  const tooltipY = highPoint ? Math.min(Math.max(highPoint.y - tooltipHeight / 2 - 8, 0), height - tooltipHeight - 2) : 0;

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <defs>
        <linearGradient id={areaId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.25" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0.02" />
        </linearGradient>
        <filter id={glowId} x="-400%" y="-400%" width="900%" height="900%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Area fill */}
      <path d={area} fill={`url(#${areaId})`} />

      {/* Grid lines (non-sparkline only) */}
      {!sparkline && [0.25, 0.5, 0.75].map((f) => {
        const y = 12 + f * (height - 24);
        return <line key={f} x1={12} y1={y} x2={width - 12} y2={y} stroke="#30363d" strokeWidth={0.5} />;
      })}

      {/* Line */}
      <path d={path} fill="none" stroke={lineColor} strokeWidth={sparkline ? 1.5 : 1.8} strokeLinecap="round" strokeLinejoin="round" />

      {/* Highest value marker */}
      {showHighestLabel && highPoint && (
        <g>
          <circle cx={highPoint.x} cy={highPoint.y} r={3.5} fill="#F5EC63" filter={`url(#${glowId})`} />
          <circle cx={highPoint.x} cy={highPoint.y} r={2.4} fill="#FFF4A8" />
          <rect
            x={tooltipX}
            y={tooltipY}
            width={tooltipWidth}
            height={tooltipHeight}
            rx={3}
            fill="rgba(6, 7, 29, 0.76)"
            stroke="#347E23"
            strokeWidth={1}
          />
          <text
            x={tooltipX + tooltipWidth / 2}
            y={tooltipY + 12.5}
            textAnchor="middle"
            fontSize={9}
            letterSpacing={0.9}
            fill="#FFFFFF"
          >
            {highPoint.price.toFixed(4)}
          </text>
        </g>
      )}

      {/* Last-point dot */}
      {!sparkline && (() => {
        const lx = 12 + ((data.length - 1) / (data.length - 1)) * (width - 24);
        const range2 = maxP - minP || 1;
        const ly = 12 + ((maxP - last) / range2) * (height - 24);
        return <circle cx={lx} cy={ly} r={3} fill={lineColor} />;
      })()}

      {/* X-axis labels */}
      {!sparkline && ticks.map((t) => (
        <text key={t.label + t.x} x={t.x} y={height - 2} textAnchor="middle" fontSize={8} fill="#6e7681">
          {t.label}
        </text>
      ))}

      {/* Y-axis price labels */}
      {!sparkline && (
        <>
          <text x={width - 2} y={14} textAnchor="end" fontSize={8} fill="#6e7681">{maxP.toFixed(2)}</text>
          <text x={width - 2} y={height - 14} textAnchor="end" fontSize={8} fill="#6e7681">{minP.toFixed(2)}</text>
        </>
      )}
    </svg>
  );
}
